import type { Feature, FeatureCollection, Geometry } from 'geojson'

import { USER_GEOMETRY_LAYER_EDIT_MAX_FEATURES } from '@/domain/protocols/UserGeometry'

type GeometryWithCoordinates = Geometry & { coordinates?: unknown }
type GenericProperties = Record<string, unknown>
type Position = [number, number]

const EMPTY_COLLECTION: FeatureCollection = {
  type: 'FeatureCollection',
  features: []
}

const SIMPLE_STYLE_KEY_ALIASES = {
  stroke: ['stroke'],
  fill: ['fill'],
  'marker-color': ['marker-color', 'markerColor'],
  'stroke-opacity': ['stroke-opacity', 'strokeOpacity'],
  'fill-opacity': ['fill-opacity', 'fillOpacity'],
  'stroke-width': ['stroke-width', 'strokeWidth'],
  'marker-size': ['marker-size', 'markerSize']
} as const

const SIMPLE_STYLE_CANONICAL_KEYS = Object.keys(SIMPLE_STYLE_KEY_ALIASES) as Array<
  keyof typeof SIMPLE_STYLE_KEY_ALIASES
>

const SIMPLE_STYLE_LEGACY_KEYS = SIMPLE_STYLE_CANONICAL_KEYS.reduce<string[]>((keys, canonicalKey) => {
  SIMPLE_STYLE_KEY_ALIASES[canonicalKey].forEach((key) => {
    if (key !== canonicalKey) {
      keys.push(key)
    }
  })

  return keys
}, [])
const UNSUPPORTED_ICON_STYLE_KEYS = ['icon-scale', 'icon-offset', 'icon-offset-units'] as const

const DEFAULT_STROKE_OPACITY = 0.9
const DEFAULT_FILL_OPACITY = 0.2
const DEFAULT_POINT_LABEL_MIN_ZOOM = 10
const MAX_POINTS_FOR_DYNAMIC_LABEL_MIN_ZOOM = USER_GEOMETRY_LAYER_EDIT_MAX_FEATURES

const toPointCoordinates = (geometry: Geometry | null | undefined): Position[] => {
  if (!geometry) return []

  if (geometry.type === 'Point') {
    const coordinates = geometry.coordinates
    return typeof coordinates?.[0] === 'number' && typeof coordinates?.[1] === 'number'
      ? [[coordinates[0], coordinates[1]]]
      : []
  }

  if (geometry.type === 'MultiPoint') {
    return geometry.coordinates.filter(
      (coordinate): coordinate is Position => typeof coordinate?.[0] === 'number' && typeof coordinate?.[1] === 'number'
    )
  }

  return []
}

const getRepresentativePointCoordinate = (geometry: Geometry | null | undefined): Position | null => {
  const coordinates = toPointCoordinates(geometry)
  if (coordinates.length === 0) {
    return null
  }

  if (coordinates.length === 1) {
    return coordinates[0]
  }

  const [sumLng, sumLat] = coordinates.reduce(([lngAcc, latAcc], [lng, lat]) => [lngAcc + lng, latAcc + lat], [0, 0])

  return [sumLng / coordinates.length, sumLat / coordinates.length]
}

const planarDistanceInPseudoMeters = (from: Position, to: Position): number => {
  const lngDiffMeters = (to[0] - from[0]) * 111320
  const latDiffMeters = (to[1] - from[1]) * 110540

  return Math.sqrt(lngDiffMeters ** 2 + latDiffMeters ** 2)
}

const resolvePointLabelMinZoom = (nearestDistanceInMeters: number | null): number => {
  if (nearestDistanceInMeters == null || !Number.isFinite(nearestDistanceInMeters)) {
    return DEFAULT_POINT_LABEL_MIN_ZOOM
  }

  if (nearestDistanceInMeters < 80) return 14
  if (nearestDistanceInMeters < 150) return 13
  if (nearestDistanceInMeters < 300) return 12
  if (nearestDistanceInMeters < 700) return 11
  if (nearestDistanceInMeters < 1500) return 10
  if (nearestDistanceInMeters < 5000) return 9
  return 8
}

const buildPointLabelMinZoomByFeatureId = (features: Feature[]): Map<string, number> => {
  const pointFeatures = features
    .map((feature, index) => {
      const coordinate = getRepresentativePointCoordinate(feature.geometry)
      if (!coordinate) {
        return null
      }

      const featureId = String(feature.id ?? (feature.properties as GenericProperties | null)?.id ?? `feat_${index}`)
      return { featureId, coordinate }
    })
    .filter((item): item is { featureId: string; coordinate: Position } => item !== null)

  const result = new Map<string, number>()

  if (pointFeatures.length > MAX_POINTS_FOR_DYNAMIC_LABEL_MIN_ZOOM) {
    pointFeatures.forEach(({ featureId }) => {
      result.set(featureId, DEFAULT_POINT_LABEL_MIN_ZOOM)
    })

    return result
  }

  pointFeatures.forEach((current, currentIndex) => {
    let nearestDistanceInMeters: number | null = null

    pointFeatures.forEach((other, otherIndex) => {
      if (currentIndex === otherIndex) return

      const distance = planarDistanceInPseudoMeters(current.coordinate, other.coordinate)
      if (nearestDistanceInMeters == null || distance < nearestDistanceInMeters) {
        nearestDistanceInMeters = distance
      }
    })

    result.set(current.featureId, resolvePointLabelMinZoom(nearestDistanceInMeters))
  })

  return result
}

const sanitizePropertyValue = (
  value: unknown
): {
  value: unknown
  changed: boolean
  omit: boolean
} => {
  if (value === undefined) {
    return {
      value: undefined,
      changed: true,
      omit: true
    }
  }

  if (Array.isArray(value)) {
    let changed = false

    const sanitizedArray = value.map((item) => {
      const sanitizedItem = sanitizePropertyValue(item)

      if (sanitizedItem.omit) {
        changed = true
        return null
      }

      if (sanitizedItem.changed) {
        changed = true
      }

      return sanitizedItem.value
    })

    return {
      value: sanitizedArray,
      changed,
      omit: false
    }
  }

  if (value && typeof value === 'object' && !(value instanceof Uint8Array) && !(value instanceof Date)) {
    let changed = false
    const sanitizedObject: GenericProperties = {}

    Object.entries(value).forEach(([key, nestedValue]) => {
      const sanitizedNestedValue = sanitizePropertyValue(nestedValue)

      if (sanitizedNestedValue.omit) {
        changed = true
        return
      }

      if (sanitizedNestedValue.changed) {
        changed = true
      }

      sanitizedObject[key] = sanitizedNestedValue.value
    })

    return {
      value: sanitizedObject,
      changed,
      omit: false
    }
  }

  return {
    value,
    changed: false,
    omit: false
  }
}

const sanitizeProperties = (
  properties: GenericProperties | null
): {
  properties: GenericProperties | null
  changed: boolean
} => {
  if (!properties) {
    return {
      properties,
      changed: false
    }
  }

  const sanitized = sanitizePropertyValue(properties)

  return {
    properties: sanitized.omit ? null : (sanitized.value as GenericProperties),
    changed: sanitized.changed
  }
}

const normalizeColor = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim()
  if (!normalized || normalized.toLowerCase() === 'none') {
    return null
  }

  return normalized
}

const normalizeOpacity = (value: unknown): number | null => {
  const opacity = Number(value)
  if (!Number.isFinite(opacity)) {
    return null
  }

  return Math.max(0, Math.min(1, opacity))
}

const normalizeBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value
  }

  return null
}

const normalizePositiveNumber = (value: unknown): number | null => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null
  }

  return parsed
}

const normalizeMarkerSize = (value: unknown): number | null => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === 'small') return 10
    if (normalized === 'medium') return 14
    if (normalized === 'large') return 18
  }

  return normalizePositiveNumber(value)
}

const getFirstDefinedProperty = (
  properties: GenericProperties | null,
  aliases: readonly string[]
): GenericProperties[keyof GenericProperties] | undefined => {
  for (let index = 0; index < aliases.length; index += 1) {
    const key = aliases[index]
    if (properties && Object.prototype.hasOwnProperty.call(properties, key)) {
      return properties[key]
    }
  }

  return undefined
}

const deleteKeysIfPresent = (target: GenericProperties, keys: readonly string[]): boolean => {
  let changed = false

  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index]
    if (Object.prototype.hasOwnProperty.call(target, key)) {
      delete target[key]
      changed = true
    }
  }

  return changed
}

const resolveFeatureStyle = (
  properties: GenericProperties | null
): { properties: GenericProperties; changed: boolean } => {
  const normalizedProperties: GenericProperties = {}
  let changed = false

  const strokeColor = normalizeColor(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES.stroke))
  const fillColor = normalizeColor(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES.fill))
  const markerColor = normalizeColor(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['marker-color']))

  if (strokeColor) {
    normalizedProperties.stroke = strokeColor
    if (getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES.stroke) !== strokeColor) {
      changed = true
    }
  }

  if (fillColor) {
    normalizedProperties.fill = fillColor
    if (getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES.fill) !== fillColor) {
      changed = true
    }
  }

  if (markerColor) {
    normalizedProperties['marker-color'] = markerColor
    if (properties?.['marker-color'] !== markerColor) {
      changed = true
    }
  }

  const strokeOpacity = normalizeOpacity(
    getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['stroke-opacity'])
  )
  const nextStrokeOpacity = strokeOpacity ?? DEFAULT_STROKE_OPACITY
  normalizedProperties['stroke-opacity'] = nextStrokeOpacity
  if (properties?.['stroke-opacity'] !== nextStrokeOpacity) {
    changed = true
  }

  const width = normalizePositiveNumber(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['stroke-width']))
  if (width != null) {
    normalizedProperties['stroke-width'] = width
    if (properties?.['stroke-width'] !== width) {
      changed = true
    }
  }

  const size = normalizeMarkerSize(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['marker-size']))
  if (size != null) {
    normalizedProperties['marker-size'] = size
    if (properties?.['marker-size'] !== size) {
      changed = true
    }
  }

  const fillEnabled = (() => {
    const currentFillColor = normalizeColor(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES.fill))
    const fillOpacity = normalizeOpacity(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['fill-opacity']))
    if (!currentFillColor && fillOpacity == null) {
      return null
    }
    return currentFillColor ? fillOpacity == null || fillOpacity > 0 : fillOpacity != null && fillOpacity > 0
  })()

  if (typeof fillEnabled === 'boolean') {
    const nextFillOpacity = fillEnabled
      ? (normalizeOpacity(getFirstDefinedProperty(properties, SIMPLE_STYLE_KEY_ALIASES['fill-opacity'])) ??
        DEFAULT_FILL_OPACITY)
      : 0
    normalizedProperties['fill-opacity'] = nextFillOpacity
    if (properties?.['fill-opacity'] !== nextFillOpacity) {
      changed = true
    }
  }

  const hidden = normalizeBoolean(properties?.hidden) ?? false
  normalizedProperties.hidden = hidden
  if (properties?.hidden !== hidden) {
    changed = true
  }

  const showLabel = normalizeBoolean(properties?.showLabel) ?? false
  normalizedProperties.showLabel = showLabel
  if (properties?.showLabel !== showLabel) {
    changed = true
  }

  return { properties: normalizedProperties, changed }
}

const hasCoordinates = (geometry: Geometry | null | undefined): boolean => {
  if (!geometry) {
    return false
  }

  if (geometry.type === 'GeometryCollection') {
    return Array.isArray(geometry.geometries) && geometry.geometries.length > 0
  }

  const coordinates = (geometry as GeometryWithCoordinates).coordinates
  return Array.isArray(coordinates) && coordinates.length > 0
}

export const optimizeGeoJsonForStorage = (geojson: FeatureCollection): FeatureCollection => {
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
    return { ...EMPTY_COLLECTION }
  }

  const features: Feature[] = []
  let changed = false

  const pointLabelMinZoomByFeatureId = buildPointLabelMinZoomByFeatureId(geojson.features)

  for (let index = 0; index < geojson.features.length; index += 1) {
    const feature = geojson.features[index]
    if (!feature?.geometry || !hasCoordinates(feature.geometry)) {
      changed = true
      continue
    }

    const sourceProperties =
      feature.properties && typeof feature.properties === 'object' ? (feature.properties as GenericProperties) : null
    const sanitizedPropertiesResult = sanitizeProperties(sourceProperties)
    const effectiveSourceProperties = sanitizedPropertiesResult.properties
    let normalizedProperties: GenericProperties | null = null
    let featureChanged = false

    const ensureProperties = () => {
      if (!normalizedProperties) {
        normalizedProperties = { ...(effectiveSourceProperties ?? {}) }
      }

      return normalizedProperties
    }

    if (sanitizedPropertiesResult.changed) {
      featureChanged = true
    }

    const generatedId = feature.id ?? (effectiveSourceProperties?.id as string | number) ?? `feat_${index}`
    const nextId = feature.id ?? generatedId
    const isPointGeometry = feature.geometry?.type === 'Point' || feature.geometry?.type === 'MultiPoint'

    if (feature.id !== nextId) {
      featureChanged = true
    }

    if (effectiveSourceProperties?.id !== generatedId) {
      ensureProperties().id = generatedId
      featureChanged = true
    }

    if (effectiveSourceProperties && 'style' in effectiveSourceProperties) {
      delete ensureProperties().style
      featureChanged = true
    }

    if (effectiveSourceProperties && 'styleUrl' in effectiveSourceProperties) {
      delete ensureProperties().styleUrl
      featureChanged = true
    }

    if (effectiveSourceProperties) {
      if (deleteKeysIfPresent(ensureProperties(), SIMPLE_STYLE_LEGACY_KEYS)) {
        featureChanged = true
      }

      if (deleteKeysIfPresent(ensureProperties(), UNSUPPORTED_ICON_STYLE_KEYS)) {
        featureChanged = true
      }
    }

    const resolvedStyle = resolveFeatureStyle(effectiveSourceProperties)
    if (resolvedStyle.changed) {
      Object.assign(ensureProperties(), resolvedStyle.properties)
      featureChanged = true
    }

    const isPolygonGeometry = feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon'
    if (isPolygonGeometry) {
      const currentShowLabel = normalizeBoolean(effectiveSourceProperties?.showLabel)
      if (currentShowLabel !== true) {
        ensureProperties().showLabel = true
        featureChanged = true
      }
    }

    if (isPointGeometry) {
      const currentShowLabel = normalizeBoolean(effectiveSourceProperties?.showLabel)
      if (currentShowLabel !== true) {
        ensureProperties().showLabel = true
        featureChanged = true
      }

      const nextLabelMinZoom = pointLabelMinZoomByFeatureId.get(String(generatedId)) ?? DEFAULT_POINT_LABEL_MIN_ZOOM
      if (effectiveSourceProperties?.labelMinZoom !== nextLabelMinZoom) {
        ensureProperties().labelMinZoom = nextLabelMinZoom
        featureChanged = true
      }
    }

    if (!featureChanged) {
      features.push(feature)
      continue
    }

    changed = true
    features.push({
      ...feature,
      id: nextId,
      ...(normalizedProperties ? { properties: normalizedProperties } : {})
    })
  }

  if (!changed && features.length === geojson.features.length) {
    return geojson
  }

  return {
    ...geojson,
    type: 'FeatureCollection',
    features
  }
}
